import { useEffect, useRef, useState, useCallback } from "react";

const ACTIVITY_EVENTS = [
  "mousemove", "mousedown", "keydown", "scroll", "touchstart", "click", "wheel",
];

/**
 * Hook de auto-logout por inactividad.
 *
 * @param {Object} opts
 * @param {number}   opts.timeoutMs     Total de inactividad antes del logout (ms)
 * @param {number}   opts.warningMs     Cuánto antes del logout se muestra la advertencia (ms)
 * @param {Function} opts.onLogout      Callback que ejecuta el logout real
 * @param {boolean}  opts.enabled       Solo activo cuando hay sesión abierta
 *
 * @returns {{ showWarning, secondsLeft, keepAlive, forceLogout }}
 */
export function useInactivityLogout({
  timeoutMs = 4 * 60 * 60 * 1000,   // 4 horas por defecto
  warningMs = 5 * 60 * 1000,         // advertencia 5 minutos antes
  onLogout,
  enabled = false,
}) {
  const [showWarning, setShowWarning] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(Math.floor(warningMs / 1000));

  const mainTimerRef    = useRef(null);
  const logoutTimerRef  = useRef(null);
  const countdownRef    = useRef(null);
  const enabledRef      = useRef(enabled);

  // Mantener ref sincronizado para evitar stale closure en el listener
  useEffect(() => { enabledRef.current = enabled; }, [enabled]);

  const clearAll = useCallback(() => {
    clearTimeout(mainTimerRef.current);
    clearTimeout(logoutTimerRef.current);
    clearInterval(countdownRef.current);
  }, []);

  const forceLogout = useCallback(() => {
    clearAll();
    setShowWarning(false);
    onLogout();
  }, [clearAll, onLogout]);

  // Arranca el countdown de los últimos N segundos antes del logout
  const startWarning = useCallback(() => {
    setShowWarning(true);
    setSecondsLeft(Math.floor(warningMs / 1000));

    countdownRef.current = setInterval(() => {
      setSecondsLeft(prev => {
        if (prev <= 1) {
          clearInterval(countdownRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    logoutTimerRef.current = setTimeout(forceLogout, warningMs);
  }, [warningMs, forceLogout]);

  // Reinicia el temporizador principal (se llama en cada evento de actividad)
  const resetTimer = useCallback(() => {
    if (!enabledRef.current) return;
    clearAll();
    setShowWarning(false);
    // Tiempo hasta mostrar la advertencia = timeout total - período de advertencia
    const idleBeforeWarn = Math.max(timeoutMs - warningMs, 0);
    mainTimerRef.current = setTimeout(startWarning, idleBeforeWarn);
  }, [clearAll, timeoutMs, warningMs, startWarning]);

  // Botón "Seguir conectado": resetea el timer y cierra el modal
  const keepAlive = useCallback(() => {
    resetTimer();
  }, [resetTimer]);

  // Registrar listeners de actividad del usuario
  useEffect(() => {
    if (!enabled) {
      clearAll();
      setShowWarning(false);
      return;
    }

    resetTimer(); // arranca al montar o al habilitarse

    // Throttle: no reiniciar más de 1 vez por segundo para no saturar
    let lastReset = 0;
    const handler = () => {
      const now = Date.now();
      if (now - lastReset > 1000) {
        lastReset = now;
        resetTimer();
      }
    };

    ACTIVITY_EVENTS.forEach(ev => window.addEventListener(ev, handler, { passive: true }));

    return () => {
      clearAll();
      ACTIVITY_EVENTS.forEach(ev => window.removeEventListener(ev, handler));
    };
  }, [enabled]); // eslint-disable-line react-hooks/exhaustive-deps

  return { showWarning, secondsLeft, keepAlive, forceLogout };
}
